/**
 * Blob URL Manager
 *
 * Centralized manager for blob URLs to prevent memory leaks.
 * Ensures every created blob URL is properly revoked when no longer needed.
 *
 * Uses reference counting to support shared blob URLs (e.g., duplicated nodes).
 * A blob URL is only revoked when all references are released.
 */

class BlobURLManager {
  private urls = new Map<string, string>();
  private refCounts = new Map<string, number>();

  /**
   * Create a blob URL and register it with a key
   * If a URL already exists for the key, it will be revoked first
   */
  create(blob: Blob, key: string): string {
    this.revoke(key); // Clean up old one if exists
    const url = URL.createObjectURL(blob);
    this.urls.set(key, url);
    this.refCounts.set(key, 1);
    return url;
  }

  /**
   * Increment reference count for a key (used when duplicating nodes)
   */
  addRef(key: string): void {
    const count = this.refCounts.get(key) || 0;
    if (count > 0) {
      this.refCounts.set(key, count + 1);
    }
  }

  /**
   * Decrement reference count and revoke if count reaches zero
   * Returns true if the blob was actually revoked
   */
  release(key: string): boolean {
    const count = this.refCounts.get(key) || 0;
    if (count <= 1) {
      // Last reference, actually revoke
      const url = this.urls.get(key);
      if (url) {
        URL.revokeObjectURL(url);
        this.urls.delete(key);
        this.refCounts.delete(key);
        return true;
      }
    } else {
      // Other references exist, just decrement
      this.refCounts.set(key, count - 1);
    }
    return false;
  }

  /**
   * Revoke a specific blob URL by key (ignores reference count)
   * Use release() for reference-counted revocation
   */
  revoke(key: string): void {
    const url = this.urls.get(key);
    if (url) {
      URL.revokeObjectURL(url);
      this.urls.delete(key);
      this.refCounts.delete(key);
    }
  }

  /**
   * Revoke all registered blob URLs
   * Useful for cleanup on unmount or project close
   */
  revokeAll(): void {
    this.urls.forEach(url => URL.revokeObjectURL(url));
    this.urls.clear();
    this.refCounts.clear();
  }

  /**
   * Get a blob URL by its key
   * Returns undefined if the key doesn't exist
   */
  get(key: string): string | undefined {
    return this.urls.get(key);
  }

  /**
   * Get the reference count for a key
   */
  getRefCount(key: string): number {
    return this.refCounts.get(key) || 0;
  }

  /**
   * Get the current count of registered URLs
   * Useful for debugging memory usage
   */
  getCount(): number {
    return this.urls.size;
  }

  /**
   * Get all registered blob URLs
   * Returns a Map of key -> blob URL
   */
  getAll(): Map<string, string> {
    return new Map(this.urls);
  }
}

export const blobManager = new BlobURLManager();
